import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { reportApi, paymentApi, memberApi, expensePaymentApi } from '../../services/api';
import { formatDate, formatDateRange } from '../../utils/dateUtils';

// Register Chart.js components
Chart.register(...registerables);

const FinancialReport = () => {
  const [report, setReport] = useState(null);
  const [monthsBack, setMonthsBack] = useState(12);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthlyPayments, setMonthlyPayments] = useState([]);
  const [members, setMembers] = useState({});
  const [loadingPayments, setLoadingPayments] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const response = await reportApi.getFinancialReport(monthsBack);
        setReport(response.data);
        
        // Get all payments and expense payments to calculate actual monthly income and expenses
        const paymentsResponse = await paymentApi.getAll();
        const allPayments = paymentsResponse.data;
        
        const expensePaymentsResponse = await expensePaymentApi.getAll();
        const allExpensePayments = expensePaymentsResponse.data;
        
        // Prepare chart data
        if (response.data.monthly_summaries && response.data.monthly_summaries.length > 0) {
          const labels = response.data.monthly_summaries.map(summary => summary.month);
          
          // Calculate actual income based on payment date (paid_on)
          const actualIncomeData = labels.map(monthStr => {
            const [year, month] = monthStr.split('-').map(Number);
            const startDate = new Date(year, month - 1, 1); // Month is 0-indexed in JS Date
            const endDate = new Date(year, month, 0); // Last day of the month
            
            // Filter payments for this month based on paid_on date
            const monthlyPayments = allPayments.filter(payment => {
              const paymentDate = new Date(payment.paid_on);
              return paymentDate >= startDate && paymentDate <= endDate;
            });
            
            // Sum the amounts
            return monthlyPayments.reduce((sum, payment) => sum + payment.amount, 0);
          });
          
          // Calculate actual expenses based on payment date (paid_on)
          const actualExpenseData = labels.map(monthStr => {
            const [year, month] = monthStr.split('-').map(Number);
            const startDate = new Date(year, month - 1, 1); // Month is 0-indexed in JS Date
            const endDate = new Date(year, month, 0); // Last day of the month
            
            // Filter expense payments for this month based on paid_on date
            const monthlyExpensePayments = allExpensePayments.filter(payment => {
              const paymentDate = new Date(payment.paid_on);
              return paymentDate >= startDate && paymentDate <= endDate;
            });
            
            // Sum the amounts
            return monthlyExpensePayments.reduce((sum, payment) => sum + payment.amount, 0);
          });
          
          // Calculate balance as actual income - actual expenses
          const balanceData = actualIncomeData.map((income, index) => income - actualExpenseData[index]);
          
          // Add expected income and expense data (constant values for all months)
          const expectedIncomeData = response.data.monthly_summaries.map(() => response.data.total_monthly_income);
          const expectedExpenseData = response.data.monthly_summaries.map(() => response.data.total_monthly_expenses);
          
          setChartData({
            labels,
            datasets: [
              {
                label: 'Actual Income',
                data: actualIncomeData,
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
              },
              {
                label: 'Expected Income',
                data: expectedIncomeData,
                backgroundColor: 'rgba(153, 102, 255, 0.2)',
                borderColor: 'rgba(153, 102, 255, 1)',
                borderWidth: 1,
                type: 'line',
                pointRadius: 0,
                borderDash: [5, 5],
              },
              {
                label: 'Actual Expenses',
                data: actualExpenseData,
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1,
              },
              {
                label: 'Expected Expenses',
                data: expectedExpenseData,
                backgroundColor: 'rgba(255, 159, 64, 0.2)',
                borderColor: 'rgba(255, 159, 64, 1)',
                borderWidth: 1,
                type: 'line',
                pointRadius: 0,
                borderDash: [5, 5],
              },
              {
                label: 'Balance',
                data: balanceData,
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
              },
            ],
          });
        }
        
        // Set the default selected month to the most recent month
        if (response.data.monthly_summaries && response.data.monthly_summaries.length > 0) {
          setSelectedMonth(response.data.monthly_summaries[0].month);
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load financial report');
        setLoading(false);
        console.error(err);
      }
    };

    fetchReport();
  }, [monthsBack]);
  
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await memberApi.getAll();
        const membersMap = {};
        response.data.forEach(member => {
          membersMap[member.id] = member.username || member.name || 'Unknown';
        });
        setMembers(membersMap);
      } catch (err) {
        console.error('Failed to load members', err);
      }
    };
    
    fetchMembers();
  }, []);
  
  useEffect(() => {
    const fetchMonthlyPayments = async () => {
      if (!selectedMonth) return;
      
      try {
        setLoadingPayments(true);
        
        // Get all payments
        const response = await paymentApi.getAll();
        const allPayments = response.data;
        
        // Filter payments for the selected month
        // Format of selectedMonth is "YYYY-MM"
        const [year, month] = selectedMonth.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1); // Month is 0-indexed in JS Date
        const endDate = new Date(year, month, 0); // Last day of the month
        
        const filteredPayments = allPayments.filter(payment => {
          const paymentDate = new Date(payment.paid_on);
          return paymentDate >= startDate && paymentDate <= endDate;
        });
        
        // Sort by date (newest first)
        filteredPayments.sort((a, b) => new Date(b.paid_on) - new Date(a.paid_on));
        
        setMonthlyPayments(filteredPayments);
        setLoadingPayments(false);
      } catch (err) {
        console.error('Failed to load monthly payments', err);
        setLoadingPayments(false);
      }
    };
    
    fetchMonthlyPayments();
  }, [selectedMonth]);

  const handleMonthsChange = (e) => {
    setMonthsBack(parseInt(e.target.value, 10));
  };
  
  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  if (loading) {
    return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  }

  if (error) {
    return <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>;
  }

  if (!report) {
    return <div className="alert alert-warning"><i className="fas fa-exclamation-circle me-2"></i>No report data available</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5 mb-4">Financial Report</h1>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-calendar-alt me-2"></i>
                Report Period
              </h5>
            </div>
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-4">
                  <label htmlFor="monthsBack" className="form-label">Show data for the last:</label>
                </div>
                <div className="col-md-4">
                  <select
                    className="form-select"
                    id="monthsBack"
                    value={monthsBack}
                    onChange={handleMonthsChange}
                  >
                    <option value="3">3 months</option>
                    <option value="6">6 months</option>
                    <option value="12">12 months</option>
                    <option value="24">24 months</option>
                    <option value="36">36 months</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-md-3 mb-3 mb-md-0">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center p-4">
              <h3 className="text-muted mb-3">Monthly Income</h3>
              <div className="display-6 fw-bold text-success">
                {report.total_monthly_income.toFixed(2)} €
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3 mb-md-0">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center p-4">
              <h3 className="text-muted mb-3">Monthly Expenses</h3>
              <div className="display-6 fw-bold text-danger">
                {report.total_monthly_expenses.toFixed(2)} €
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3 mb-md-0">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center p-4">
              <h3 className="text-muted mb-3">Monthly Balance</h3>
              <div className={`display-6 fw-bold ${report.monthly_balance >= 0 ? 'text-primary' : 'text-danger'}`}>
                {report.monthly_balance.toFixed(2)} €
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center p-4">
              <h3 className="text-muted mb-3">Members</h3>
              <div className="display-6 fw-bold text-primary">
                {report.member_summaries.length}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {chartData && (
        <div className="row mb-4">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-bar me-2"></i>
                  Financial Overview
                </h5>
              </div>
              <div className="card-body">
                <div style={{ height: '350px' }}>
                  <Bar 
                    data={chartData} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="row mb-4">
        <div className="col-md-6 mb-4 mb-md-0">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-users me-2"></i>
                Member Payment Status
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead >
                    <tr>
                      <th>Name</th>
                      <th>Current Fee</th>
                      <th>Paid Until</th>
                      <th>Months in Advance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.member_summaries.map(member => (
                      <tr key={member.member_id}>
                        <td className="fw-bold">{member.member_name}</td>
                        <td>{member.current_fee.toFixed(2)} €</td>
                        <td>{member.paid_until ? formatDate(member.paid_until) : <span className="text-muted">N/A</span>}</td>
                        <td>
                          <span className={`badge ${
                            member.months_paid_in_advance > 2 ? 'bg-success' : 
                            member.months_paid_in_advance > 0 ? 'bg-warning text-dark' : 
                            'bg-danger'
                          }`}>
                            {member.months_paid_in_advance}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-file-invoice-dollar me-2"></i>
                Expense Status
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead >
                    <tr>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Monthly Cost</th>
                      <th>Paid Until</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.expense_summaries.map(expense => (
                      <tr key={expense.expense_id}>
                        <td className="fw-bold">{expense.expense_name}</td>
                        <td>
                          <span className="badge bg-secondary rounded-pill">
                            {expense.category}
                          </span>
                        </td>
                        <td className="fw-bold text-danger">{expense.monthly_cost.toFixed(2)} €</td>
                        <td>{expense.paid_until ? formatDate(expense.paid_until) : <span className="text-muted">N/A</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-table me-2"></i>
                Monthly Summaries
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead >
                    <tr>
                      <th>Month</th>
                      <th>Income</th>
                      <th>Expenses</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.monthly_summaries.map(summary => (
                      <tr key={summary.month}>
                        <td className="fw-bold">{summary.month}</td>
                        <td className="text-success">{summary.income.toFixed(2)} €</td>
                        <td className="text-danger">{summary.expenses.toFixed(2)} €</td>
                        <td className={summary.balance >= 0 ? 'text-primary fw-bold' : 'text-danger fw-bold'}>
                          {summary.balance.toFixed(2)} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-money-bill-wave me-2"></i>
                Monthly Payments Detail
              </h5>
            </div>
            <div className="card-body">
              <div className="row mb-4">
                <div className="col-md-4">
                  <label htmlFor="selectedMonth" className="form-label">Select Month:</label>
                  <select
                    className="form-select"
                    id="selectedMonth"
                    value={selectedMonth}
                    onChange={handleMonthChange}
                  >
                    {report.monthly_summaries.map(summary => (
                      <option key={summary.month} value={summary.month}>
                        {summary.month}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {loadingPayments ? (
                <div className="text-center py-4">
                  <i className="fas fa-spinner fa-spin fa-2x"></i>
                  <p className="mt-2">Loading payments...</p>
                </div>
              ) : monthlyPayments.length === 0 ? (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  No payments found for the selected month.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead >
                      <tr>
                        <th>Date</th>
                        <th>Member</th>
                        <th>Amount</th>
                        <th>Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyPayments.map(payment => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.paid_on)}</td>
                          <td className="fw-bold">{members[payment.member_id] || 'Unknown'}</td>
                          <td className="fw-bold text-success">{payment.amount.toFixed(2)} €</td>
                          <td>
                            {payment.valid_from && payment.valid_until
                              ? formatDateRange(payment.valid_from, payment.valid_until)
                              : <span className="text-muted">N/A</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot >
                      <tr>
                        <th colSpan="2" className="text-end">Total:</th>
                        <th className="text-success">
                          {monthlyPayments.reduce((sum, payment) => sum + payment.amount, 0).toFixed(2)} €
                        </th>
                        <th></th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialReport;
